import { useEffect, useState } from 'react';
import {
  Control,
  SubmitHandler,
  useForm,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReportModel,
  ReportType,
  Schedulable,
  SharedDriveModel,
} from '../../../../types';
import { useStore } from '../../state/zustand-store';
import dayjs, { Dayjs } from 'dayjs';
import {
  startReport,
  createReport,
  doApiCheck,
  editReport,
  UserKeys,
  getAllReports,
  getReportTypes,
  getDomains,
  getAllRoles,
  getSharedDrivesByIdsQuery,
} from '../../../api/users-api';
import { ModuleList } from '../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';
import { handleErrors } from '../../../utils/errors';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import StepConfiguration from './steps/step-configuration';
import StepReview from './steps/step-review';
import StepSelectColumns from './steps/step-select-columns';
import StepSources from './steps/step-sources';
import SharedScheduleStep from '../../../shared/shared-schedule-step';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useChangeLogGate } from '../../../shared/hooks/use-change-log-gate';
import { pagesConfig } from '../../../shared/pages-config';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import SimpleFormPage from '../../../shared/simple-form-page';
import SharedFormStepper, {
  StepConfig,
} from '../../../shared/shared-form-stepper';
import StepShareNotify from './steps/step-share-notify';

import StepBasicInformation from './steps/step-basic-information';
import ReportFormSkeleton from './steps/report-form-skeleton';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DateRangeZodType,
  SpreadSheetColumnSchema,
} from '../../../shared/shared-schema';
import { getReportSchema } from './steps/reports-schema';
import Box from '@mui/material/Box';
export interface ReportForm extends ReportModel {
  xDaysSelected?: boolean;
  dateRange?: DateRange<Dayjs>;
}

export type SelectedSharedDrive = {
  id: string;
  name: string;
};

export const PermissionSchema = z.object({
  userEmail: z.string().nullable(),
  role: z.enum([
    'reader',
    'commenter',
    'writer',
    'owner',
    'fileOrganizer',
    'organizer',
  ]),
  type: z.enum(['user', 'group', 'domain', 'anyone']).catch('user'),
  withLink: z.boolean(),
  sendNotification: z.boolean(),
  transferOwnership: z.boolean(),
  additionalRoles: z.array(z.string()),
  isCommenter: z.boolean(),
  photoLink: z.string(),
  docId: z.string(),
  mimeType: z.string(),
  isPrimaryOwner: z.boolean(),
  toBeDeleted: z.boolean(),
  permExpirationTime: z.number(),
  permId: z.string(),
  docOwner: z.string(),
  docTitle: z.string(),
  allowFileDiscovery: z.boolean(),
  displayName: z.string().nullable(),
  permissionDetails: z.array(z.any()),
});

export const SchedulableSchema = z.object({
  id: z.number().optional(),
  dates: z.array(z.string()).nullable(),
  times: z.array(z.string()).nullable(),
  scheduleEndDate: z.number().catch(0),
  daysOfWeek: z.boolean().catch(false),
});

export const ReportBasicInfoSchema = z.object({
  reportType: z.string().min(1, 'Report Type is required.'),
  reportName: z.string().min(1, 'Report Name is required.'),
  description: z.string().catch(''),
  generatedFileName: z.string().min(1, 'Generated File Name is required.'),

  manualRun: z.boolean().catch(true),
  runWithoutSaving: z.boolean().catch(false),
  scheduled: z.boolean().catch(false),

  destFolderId: z.string().nullable(),
  destFolderName: z.string().nullable(),
  destFolderUrl: z.string().nullable(),
  destFolderMimeType: z.string().nullable(),
});

export const ReportConfigurationSchema = z.object({
  daysback: z.number().nullable(),

  neverLoggedInCheck: z.boolean().catch(true),
  inactiveSinceDate: z.number().catch(0),

  regexTypeSelectedList: z.array(z.string()).nullable(),
  regex: z.string().nullable(),

  externalEmailAddress: z.string().nullable(),
  xDaysSelected: z.boolean().optional(),
  reportOnLastXDays: z
    .number({
      error: 'Please provide a number.',
    })
    .optional(),
  dateRange: DateRangeZodType.optional(),
  startDate: z.number().catch(0),
  endDate: z.number().catch(0),
});

export const ReportSelectColumnsSchema = z.object({
  columns: z.array(SpreadSheetColumnSchema).nullable(),
});

export const ReportShareNotifySchema = z.object({
  emailsToNotify: z.array(z.string()).nullable(),
  emailsToShare: z.array(z.string()).nullable(),
  permissions: z.array(z.any()).nullable(),
  _notifyViaEmail: z.boolean().catch(false),
});

export const ReportSourcesSchema = z.object({
  sources: z.array(z.string()).nullable(),
  sourceType: z
    .enum([
      'User',
      'Group',
      'OrgUnit',
      'Domain',
      'gPanel_Role',
      'Mobile_Device',
      'All_Users',
      'All_Groups',
      'Resource_Calendar',
      'Shared_Drive',
    ])
    .nullable(),
  allSource: z.boolean(),
});

export const MetaSchema = z.object({
  createdDate: z.number(),
  lastRunDate: z.number().catch(0),
  createdBy: z.string().catch(''),
  spreadsheetId: z.string().nullable(),
  runImmediately: z.boolean().catch(false),
  useReportName: z.boolean().catch(false),
  autoShare: z.boolean().catch(false),
  dynamicReportName: z.boolean().catch(false),
});

export const ReportSchema = ReportBasicInfoSchema.and(ReportConfigurationSchema)
  .and(ReportSelectColumnsSchema)
  .and(ReportShareNotifySchema)
  .and(ReportSourcesSchema)
  .and(SchedulableSchema)
  .and(MetaSchema);

export type ReportFormValues = z.infer<typeof ReportSchema>;

function ReportsFormContent() {
  const { createEvent } = useAnalytics();
  const { toolsRoles, changeLogRoles, usersTier, loggedInUser, currentTier } =
    useUserSettings();
  const navigate = useNavigate();
  const { id } = useParams();
  const selectedRowId = Number(id);
  const dialogType = selectedRowId ? 'edit' : 'create';

  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: [UserKeys.Reports],
    queryFn: () => getAllReports(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('reports'),
    },
  });

  const selectedReport = reports?.find((report) => report.id === selectedRowId);
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [activeStep, setActiveStep] = useState(0);

  const { data: reportTypes, isLoading: reportTypesLoading } = useQuery({
    queryKey: [UserKeys.ReportTypes],
    queryFn: () => getReportTypes(),
  });

  const {
    changeLogMessage,
    setChangeLogMessage,
    openChangeLogDialog,
    openDialog: openChangeLogDialogFn,
    dismiss: dismissChangeLog,
  } = useChangeLogGate({
    permissionValue:
      dialogType === 'create'
        ? ToolsRoles.CreateReport.value
        : ToolsRoles.EditReport.value,
    changeLogRoles,
    initialOpen: false,
  });

  const [_, setSearchParams] = useSearchParams();

  const onViewReport = (r: ReportModel) => {
    setSearchParams((prev) => {
      prev.set('selectedReport', r.id.toString());
      return prev;
    });
  };

  const [selectedReportType, setSelectedReportType] =
    useState<ReportType | null>(() => {
      const reportTypeData = reportTypes?.find(
        (reportType) => reportType.name === selectedReport?.reportType
      );
      return reportTypeData || null;
    });

  const initialType = reportTypes?.find(
    (reportType) => reportType.name === selectedReport?.reportType
  )?.name;

  const [initialSelectedType, setInitialSelectedType] = useState<string | null>(
    initialType ? initialType : null
  );

  const hasDateRange = selectedReportType?.hasDateRange ?? false;
  const hasDateSelection = selectedReportType?.hasDateSelection ?? false;

  const { data: domains } = useQuery({
    queryKey: [UserKeys.Domains],
    queryFn: () => getDomains(),
  });

  const { data: rolesData } = useQuery({
    queryKey: [UserKeys.Roles],
    queryFn: () => getAllRoles(),
  });

  const roles =
    rolesData?.filter((role) => !(role.default && role.roleType === 'admin')) ||
    [];
  const formConfig =
    selectedReport && dialogType === 'edit'
      ? {
          values: {
            ...selectedReport,
            dateRange: [
              dayjs(new Date(selectedReport.startDate)),
              dayjs(new Date(selectedReport.endDate)),
            ] as [Dayjs, Dayjs],
            xDaysSelected:
              selectedReport?.reportOnLastXDays != null &&
              selectedReport.reportOnLastXDays > 0,
          },
        }
      : {
          defaultValues: {
            createdBy: loggedInUser,
            createdDate: dayjs(new Date()).valueOf(),
            columns: [],
            destFolderId: null,
            destFolderName: null,
            destFolderUrl: null,
            destFolderMimeType: null,
            spreadsheetId: null,
            reportName: '',
            description: '',
            runWithoutSaving: false,
            manualRun: true,
            scheduled: false,
            reportType: '',
            dates: [],
            times: [],
            sourceType: null,
            sources: [],
            emailsToNotify: [],
            _notifyViaEmail: false,
            runImmediately: false,
            useReportName: false,
            emailsToShare: [],
            permissions: [],
            startDate: 0,
            endDate: 0,
            lastRunDate: 0,
            regexTypeSelectedList: [],
            daysback: null,
            reportOnLastXDays: 0,
            externalEmailAddress: null,
            xDaysSelected: false,
            dateRange: [dayjs().subtract(7, 'day'), dayjs()] as [Dayjs, Dayjs],
            allSource: false,
            neverLoggedInCheck: true,
            inactiveSinceDate: 0,
            generatedFileName: '',
          },
        };

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
    clearErrors,
  } = useForm<ReportFormValues>({
    resolver: zodResolver(getReportSchema(reportTypes, currentTier)),
    ...formConfig,
  });

  const runWithoutSaving = watch('runWithoutSaving') || false;
  const reportType = watch('reportType') || '';
  const cloudIdentityReportTypes = [
    'emptyGroupReport',
    'groupMembershipReport',
  ];

  const { data: initialSharedDrives, isLoading: initialSharedDrivesIsLoading } =
    useQuery({
      ...getSharedDrivesByIdsQuery(
        selectedReport?.sources ? selectedReport.sources : []
      ),
      enabled: !!(
        selectedReport &&
        selectedReport.sources &&
        selectedReport.sources.length > 0 &&
        selectedReport?.sourceType === 'Shared_Drive'
      ),
    });

  const {
    data: accessGranted,
    isLoading: checkingApiAccess,
    isFetching: fetchingAccess,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'cloudIdentity'],
    queryFn: () => doApiCheck('cloudIdentity', null),
    enabled: reportType !== '' && cloudIdentityReportTypes.includes(reportType),
    staleTime: 0, // Set staleTime to 0 to prevent it from being cached
  });

  const driveLabelsReportTypes = ['labelDriveDocs'];

  const {
    data: driveLabelsAccessGranted,
    isLoading: checkingDriveLabelsAccess,
    isFetching: fetchingDriveLabelsAccess,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'driveLabels'],
    queryFn: () => doApiCheck('driveLabels', null),
    enabled: reportType !== '' && driveLabelsReportTypes.includes(reportType),
    staleTime: 0, // Set staleTime to 0 to prevent it from being cached
  });

  const handleCancel = () => {
    navigate(`/${pagesConfig.reports.link}`, {
      state: { fromCancelButton: true },
    });
  };

  let steps: StepConfig[] = [
    { label: 'Basic Information', schema: ReportBasicInfoSchema },
    { label: 'Review' },
  ];

  const isScheduled = watch('scheduled');

  if (selectedReportType?.name === 'externallyOwnedDocs') {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Sources', schema: ReportSourcesSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === true &&
    selectedReportType?.hasPossibleSources === true &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === false &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Sources', schema: ReportSourcesSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === true &&
    selectedReportType?.hasPossibleSources === false &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === false &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === false &&
    selectedReportType?.hasPossibleSources === false &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === false &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (selectedReportType?.hasAdditionalConfiguration === true) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Sources', schema: ReportSourcesSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === true &&
    selectedReportType?.hasPossibleSources === true &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === true &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Sources', schema: ReportSourcesSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === true &&
    selectedReportType?.hasPossibleSources === false &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === true &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === false &&
    selectedReportType?.hasPossibleSources === false &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === true &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === true &&
    selectedReportType?.hasPossibleSources === false &&
    selectedReportType?.hasDateSelection === true &&
    selectedReportType?.hasDateRange === false &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === false &&
    selectedReportType?.hasPossibleSources === true &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === true &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Sources', schema: ReportSourcesSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === false &&
    selectedReportType?.hasPossibleSources === true &&
    selectedReportType?.hasDateSelection === false &&
    selectedReportType?.hasDateRange === false &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Sources', schema: ReportSourcesSchema },
      { label: 'Review' },
    ];
  } else if (
    selectedReportType?.hasColumns === true &&
    selectedReportType?.hasPossibleSources === true &&
    selectedReportType?.hasDateSelection === true &&
    selectedReportType?.hasDateRange === false &&
    selectedReportType?.hasAdditionalConfiguration === false
  ) {
    steps = [
      { label: 'Basic Information', schema: ReportBasicInfoSchema },
      { label: 'Report Configuration', schema: ReportConfigurationSchema },
      { label: 'Select Columns', schema: ReportSelectColumnsSchema },
      { label: 'Sources', schema: ReportSourcesSchema },
      { label: 'Email & Sharing', schema: ReportShareNotifySchema },
      { label: 'Review' },
    ];
  }

  if (isScheduled) {
    const copiedSteps = [...steps];
    copiedSteps.splice(1, 0, { label: 'Schedule', schema: SchedulableSchema });
    steps = copiedSteps;
  }

  function handleDate(data: ReportFormValues, startOrEnd: 'start' | 'end') {
    // if steps don't contain Date Range or Date Selection or they've selected # of days, then startDate and endDate are set to 0
    if ((!hasDateSelection && !hasDateRange) || data.xDaysSelected) {
      return 0;
    }

    // only startDate is set for Date Selection
    if (hasDateSelection) {
      // startDate is 0 if user never selected a date, default is 3 days ago
      const startValue =
        data.startDate === 0
          ? dayjs().subtract(3, 'day').valueOf()
          : data.startDate;

      if (startOrEnd === 'start') {
        return startValue;
      } else {
        return 0;
      }
    }

    if (!data.dateRange) return 0;
    const index = startOrEnd === 'start' ? 0 : 1;
    return data.dateRange[index]?.valueOf();
  }

  const [stepCompleted, setStepCompleted] = useState(() =>
    steps.map(() => (selectedRowId ? true : false))
  );

  const { mutate, isPending } = useMutation({
    mutationFn: (data: ReportFormValues) => {
      const updatedFormData = {
        ...data,
        startDate: handleDate(data, 'start'),
        endDate: handleDate(data, 'end'),
        reportOnLastXDays: data.xDaysSelected ? data.reportOnLastXDays : 0,
        inactiveSinceDate: data.inactiveSinceDate ? data.inactiveSinceDate : 0,
      };

      delete updatedFormData.dateRange;
      delete updatedFormData.xDaysSelected;

      return createReport(updatedFormData, changeLogMessage);
    },
    onSuccess: (returnData) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Report added successfully!',
      });

      queryClient.setQueryData([UserKeys.Reports], (oldData: ReportModel[]) => {
        if (!oldData) return;
        return [...oldData, returnData];
      });
      setChangeLogMessage('');
      setActiveStep(0);
      reset();
      bypass();
      navigate(`/${pagesConfig.reports.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding this report. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.report_center.reports.create_report, {
        module: ModuleList.modules.reportCenter.name,
        section: ModuleList.modules.reportCenter.sections.reports,
        reportType: getValues('reportType'),
      });
      dismissChangeLog();
    },
  });

  const { mutate: editMutate, isPending: editIsPending } = useMutation({
    mutationFn: (data: ReportFormValues) => {
      const updatedFormData = {
        ...data,
        startDate: handleDate(data, 'start'),
        endDate: handleDate(data, 'end'),
        reportOnLastXDays: data.xDaysSelected ? data.reportOnLastXDays : 0,
        inactiveSinceDate: data.inactiveSinceDate ? data.inactiveSinceDate : 0,
      };

      if (data.allSource) {
        data.sourceType =
          data.sourceType === 'User' ? 'All_Users' : 'All_Groups';
      }

      delete updatedFormData.dateRange;
      delete updatedFormData.xDaysSelected;

      return editReport(updatedFormData, changeLogMessage);
    },
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Report updated successfully!',
      });

      queryClient.setQueryData(
        [UserKeys.Reports],
        (oldData: ReportModel[] | null) => {
          if (oldData) {
            return oldData.map((report) => {
              if (report.id === selectedReport?.id) {
                return data;
              }
              return report;
            });
          } else {
            return [];
          }
        }
      );

      setChangeLogMessage('');
      bypass();
      navigate(`/${pagesConfig.reports.link}`);
      setActiveStep(0);
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating this report. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.report_center.reports.update_report, {
        module: ModuleList.modules.reportCenter.name,
        section: ModuleList.modules.reportCenter.sections.reports,
      });
      dismissChangeLog();
    },
  });

  const { mutate: runWithoutSavingMutate, isPending: runWithoutSavingPending } =
    useMutation({
      mutationFn: (data: ReportFormValues) => {
        const {
          dateRange,
          xDaysSelected,
          reportOnLastXDays,
          inactiveSinceDate,
          ...rest
        } = data;

        const reportModel: Partial<ReportModel> = {
          ...rest,
          startDate: xDaysSelected ? 0 : dateRange?.[0]?.valueOf() ?? 0,
          endDate: xDaysSelected ? 0 : dateRange?.[1]?.valueOf() ?? 0,
          reportOnLastXDays:
            xDaysSelected && reportOnLastXDays ? data.reportOnLastXDays : 0,
          inactiveSinceDate: inactiveSinceDate ?? 0,
        };

        return startReport(reportModel, changeLogMessage);
      },
      onSuccess: (data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'The generated report has opened in a new tab.',
        });

        window.open(
          `https://docs.google.com/spreadsheets/d/${data.spreadsheetId}/edit#gid=0`,
          '_blank',
          'noopener, noreferrer'
        );
        setChangeLogMessage('');
        bypass();
        navigate(`/${pagesConfig.reports.link}`);
        setActiveStep(0);
        reset();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue generating the report. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(analytics.report_center.reports.create_report, {
          module: ModuleList.modules.reportCenter.name,
          section: ModuleList.modules.reportCenter.sections.reports,
          reportType: getValues('reportType'),
        });
        dismissChangeLog();
      },
    });

  const onSubmit: SubmitHandler<ReportFormValues> = (data) => {
    if (dialogType === 'create') {
      if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CreateReport.value)) {
        openChangeLogDialogFn();
      } else {
        if (!data.runWithoutSaving) {
          mutate(data);
        } else {
          runWithoutSavingMutate(data);
        }
      }
    } else {
      if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.EditReport.value)) {
        openChangeLogDialogFn();
      } else {
        editMutate(data);
      }
    }
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    trigger,
    errors,
    stepCompleted,
    setStepCompleted,
  };

  const data = getValues();

  // Used to display the selected shared drives on the current form�s review step.
  // This is kept in the parent so that if the user skips the source step,
  // selectedSharedDrives is still available for the review step.
  const [selectedSharedDrives, setSelectedSharedDrives] = useState<
    SharedDriveModel[]
  >([]);

  const isLoading =
    reportsLoading || reportTypesLoading || initialSharedDrivesIsLoading;

  // If report type hasDateRange, set default date when invalid or null
  if (
    selectedReportType?.hasDateRange &&
    data.dateRange &&
    (!data.dateRange[0]?.isValid() || !data.dateRange[1]?.isValid())
  ) {
    if (!data.dateRange[0]?.isValid() && !data.dateRange[1]?.isValid()) {
      setValue('dateRange', [dayjs().subtract(7, 'day'), dayjs()]);
    } else if (!data.dateRange[0]?.isValid() && data.dateRange[1]?.isValid()) {
      setValue('dateRange', [dayjs().subtract(7, 'day'), data.dateRange[1]]);
    } else if (data.dateRange[0]?.isValid() && !data.dateRange[1]?.isValid()) {
      setValue('dateRange', [data.dateRange[0], dayjs()]);
    }
  }

  useEffect(() => {
    // When the user changes the report type during edit/create:
    // - If a different type is selected from the initial one,
    //   reset step completion so only the first step is marked complete.
    if (!selectedReportType) return;

    // store the very first selected type in create mode
    if (dialogType === 'create' && !initialSelectedType) {
      setInitialSelectedType(selectedReportType.name);
      return;
    }

    // create and edit mode: reset if user changes type
    if (
      initialSelectedType !== null &&
      selectedReportType.name !== initialSelectedType
    ) {
      setStepCompleted(() => steps.map((_, i) => i === 0));
    }

    // When edit report from grid and the selected type matches the initial type,
    // mark all steps as completed (skip to the final state).
    if (
      dialogType === 'edit' &&
      selectedReportType?.name === initialSelectedType
    ) {
      setStepCompleted(() => steps.map(() => true));
    }
  }, [selectedReportType]);

  useEffect(() => {
    // When editing an existing report and do a refresh:
    // Sync the selectedReportType and the reportType to populate the data and steps properly
    if (dialogType === 'edit' && selectedReport && reportTypes) {
      const matchedType = reportTypes.find(
        (rt) => rt.name === selectedReport.reportType
      );
      if (matchedType) {
        setSelectedReportType(matchedType);
        setInitialSelectedType(matchedType.name);
        setValue('reportType', matchedType.name, { shouldValidate: true });
      }
    }
  }, [dialogType, selectedReport, reportTypes, setValue]);

  useEffect(() => {
    if (initialSharedDrives) setSelectedSharedDrives(initialSharedDrives || []);
  }, [initialSharedDrives]);

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.reportCenter.name },
        {
          label: pagesConfig.reports.name,
          path: pagesConfig.reports.link,
        },
      ]}
    >
      <SimpleFormPage
        title={
          dialogType === 'create'
            ? pagesConfig.createReport.name
            : pagesConfig.editReport.name
        }
        stepper={<SharedFormStepper<ReportFormValues> {...formStepperProps} />}
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoading && <ReportFormSkeleton />}
            {!isLoading &&
              reportTypes &&
              steps[activeStep].label === 'Basic Information' && (
                <StepBasicInformation
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                  reportTypes={reportTypes}
                  setSelectedReportType={setSelectedReportType}
                  selectedReportType={selectedReportType}
                  usersTier={usersTier}
                  toolsRoles={toolsRoles}
                  onViewReport={onViewReport}
                  apiAccessGranted={accessGranted || false}
                  checkingApiAccess={checkingApiAccess || fetchingAccess}
                  cloudIdentityReportTypes={cloudIdentityReportTypes}
                  driveLabelsAccessGranted={driveLabelsAccessGranted || false}
                  checkingDriveLabelsAccess={
                    checkingDriveLabelsAccess || fetchingDriveLabelsAccess
                  }
                  driveLabelsReportTypes={driveLabelsReportTypes}
                  dialogType={dialogType}
                  trigger={trigger}
                  reports={reports}
                  selectedRowId={selectedRowId}
                />
              )}
            {!isLoading &&
              steps[activeStep].label === 'Report Configuration' && (
                <StepConfiguration
                  control={control}
                  selectedReportType={selectedReportType}
                  errors={errors}
                  watch={watch}
                  hasDateRange={!!hasDateRange}
                  hasDateSelection={!!hasDateSelection}
                  clearErrors={clearErrors}
                  setValue={setValue}
                />
              )}
            {!isLoading && steps[activeStep].label === 'Schedule' && (
              <SharedScheduleStep
                control={control as unknown as Control<Schedulable>}
                setValue={setValue as unknown as UseFormSetValue<Schedulable>}
                watch={watch as unknown as UseFormWatch<Schedulable>}
                errors={errors}
                isReport={true}
                processName="report"
              />
            )}
            {!isLoading && steps[activeStep].label === 'Select Columns' && (
              <StepSelectColumns
                control={control}
                watch={watch}
                setValue={setValue}
                selectedReportType={selectedReportType}
                errors={errors}
                apiAccessGranted={accessGranted || false}
                dialogType={dialogType}
                cloudIdentityReportTypes={cloudIdentityReportTypes}
              />
            )}
            {!isLoading &&
              domains &&
              roles &&
              steps[activeStep].label === 'Sources' && (
                <StepSources
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  errors={errors}
                  domains={domains}
                  trigger={trigger}
                  selectedReportType={selectedReportType}
                  clearErrors={clearErrors}
                  roles={roles}
                  setSelectedSharedDrives={setSelectedSharedDrives}
                  reports={reports}
                  selectedRowId={selectedRowId}
                  onViewReport={onViewReport}
                />
              )}
            {steps[activeStep].label === 'Email & Sharing' && (
              <StepShareNotify
                control={control}
                watch={watch}
                setValue={setValue}
                loggedInUser={loggedInUser}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Review' && (
              <StepReview
                selectedReport={data}
                selectedReportType={selectedReportType}
                selectedSharedDrives={selectedSharedDrives}
                dialogType={dialogType}
              />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        submitButtonText={
          activeStep === steps.length - 1
            ? dialogType === 'create'
              ? runWithoutSaving
                ? 'Run Without Saving'
                : 'Create Report'
              : 'Update Report'
            : 'Next'
        }
        isLoadingSubmit={
          isValidating ||
          isSubmitting ||
          isPending ||
          runWithoutSavingPending ||
          checkingApiAccess ||
          fetchingAccess ||
          checkingDriveLabelsAccess ||
          fetchingDriveLabelsAccess
        }
        isDisableSubmit={
          isValidating ||
          isSubmitting ||
          isPending ||
          checkingApiAccess ||
          fetchingAccess ||
          checkingDriveLabelsAccess ||
          fetchingDriveLabelsAccess ||
          (driveLabelsReportTypes.includes(reportType) &&
            !driveLabelsAccessGranted &&
            !checkingDriveLabelsAccess &&
            !fetchingDriveLabelsAccess)
        }
      />

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={dismissChangeLog}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (dialogType === 'create') {
            if (runWithoutSaving) {
              runWithoutSavingMutate(getValues());
            } else {
              mutate(getValues());
            }
          } else {
            editMutate(getValues());
          }
        }}
        mutateLoading={isPending || editIsPending || runWithoutSavingPending}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Report"
        pageMode={dialogType}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
    </SimpleBreadcrumbsPage>
  );
}

export default function ReportsFormPage() {
  const { id } = useParams();
  return <ReportsFormContent key={id} />;
}
